import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import { 
  Mail, Send, Inbox, Trash2, RefreshCw, Plus, Settings, 
  X, Paperclip, Edit, Check, ChevronDown, FileText, Star,
  Archive, AlertCircle, Folder, Download, Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailManagerNew() {
  const { getAuthHeader } = useAuth();
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Compose modal state
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeData, setComposeData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: ''
  });
  const [emailBody, setEmailBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Email chips state
  const [toChips, setToChips] = useState([]);
  const [ccChips, setCcChips] = useState([]);
  const [bccChips, setBccChips] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  
  // Address book state
  const [addressBook, setAddressBook] = useState([]);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [showCcSuggestions, setShowCcSuggestions] = useState(false);
  const [showBccSuggestions, setShowBccSuggestions] = useState(false);
  
  // Account settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    email_address: '',
    password: '',
    display_name: '',
    smtp_server: 'smtp.titan.email',
    smtp_port: 465,
    smtp_use_tls: true,
    imap_server: 'imap.titan.email',
    imap_port: 993,
    imap_use_tls: true,
    is_default: false
  });
  const [savingAccount, setSavingAccount] = useState(false);
  
  // Footer configuration
  const [showFooterSettings, setShowFooterSettings] = useState(false);
  const [footerForm, setFooterForm] = useState({
    footer_enabled: false,
    footer_html: '',
    footer_logo_url: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Notification Modal State
  const [notification, setNotification] = useState({
    show: false,
    type: 'success', // 'success', 'error', 'info', 'warning'
    title: '',
    message: ''
  });

  // Show notification
  const showNotification = (type, title, message) => {
    setNotification({ show: true, type, title, message });
  };

  // Close notification
  const closeNotification = () => {
    setNotification({ ...notification, show: false });
  };

  useEffect(() => {
    fetchEmailAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount && activeFolder !== 'settings') {
      fetchEmails();
    }
  }, [selectedAccount, activeFolder]);

  const fetchEmailAccounts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/email-accounts`, {
        headers: getAuthHeader()
      });
      setEmailAccounts(response.data);
      if (response.data.length > 0) {
        const defaultAccount = response.data.find(acc => acc.is_default) || response.data[0];
        setSelectedAccount(defaultAccount);
      }
    } catch (error) {
      console.error('Error fetching email accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch address book for autocomplete
  const fetchAddressBook = async (search = '') => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/email-address-book?search=${search}`, {
        headers: getAuthHeader()
      });
      setAddressBook(response.data);
    } catch (error) {
      console.error('Error fetching address book:', error);
    }
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Add email chip
  const addEmailChip = (email, type) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    
    if (!isValidEmail(trimmedEmail)) {
      showNotification('error', 'Invalid Email', 'Please enter a valid email address');
      return;
    }
    
    if (type === 'to') {
      if (!toChips.includes(trimmedEmail)) {
        setToChips([...toChips, trimmedEmail]);
      }
      setToInput('');
      setShowToSuggestions(false);
    } else if (type === 'cc') {
      if (!ccChips.includes(trimmedEmail)) {
        setCcChips([...ccChips, trimmedEmail]);
      }
      setCcInput('');
      setShowCcSuggestions(false);
    } else if (type === 'bcc') {
      if (!bccChips.includes(trimmedEmail)) {
        setBccChips([...bccChips, trimmedEmail]);
      }
      setBccInput('');
      setShowBccSuggestions(false);
    }
  };

  // Remove email chip
  const removeEmailChip = (email, type) => {
    if (type === 'to') {
      setToChips(toChips.filter(e => e !== email));
    } else if (type === 'cc') {
      setCcChips(ccChips.filter(e => e !== email));
    } else if (type === 'bcc') {
      setBccChips(bccChips.filter(e => e !== email));
    }
  };

  // Handle key press for email inputs
  const handleEmailInputKeyDown = (e, type) => {
    const input = type === 'to' ? toInput : type === 'cc' ? ccInput : bccInput;
    
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const emailToAdd = input.replace(',', '').trim();
      if (emailToAdd) {
        addEmailChip(emailToAdd, type);
      }
    } else if (e.key === 'Backspace' && input === '') {
      // Remove last chip on backspace if input is empty
      if (type === 'to' && toChips.length > 0) {
        setToChips(toChips.slice(0, -1));
      } else if (type === 'cc' && ccChips.length > 0) {
        setCcChips(ccChips.slice(0, -1));
      } else if (type === 'bcc' && bccChips.length > 0) {
        setBccChips(bccChips.slice(0, -1));
      }
    }
  };

  // Handle input change for autocomplete
  const handleEmailInputChange = (value, type) => {
    if (type === 'to') {
      setToInput(value);
      setShowToSuggestions(value.length > 0);
    } else if (type === 'cc') {
      setCcInput(value);
      setShowCcSuggestions(value.length > 0);
    } else if (type === 'bcc') {
      setBccInput(value);
      setShowBccSuggestions(value.length > 0);
    }
    
    if (value.length > 0) {
      fetchAddressBook(value);
    }
  };

  const fetchEmails = async () => {
    if (!selectedAccount) return;
    
    try {
      setLoading(true);
      const folder = activeFolder === 'sent' ? 'sent' : activeFolder === 'drafts' ? 'drafts' : activeFolder === 'spam' ? 'spam' : 'inbox';
      const response = await axios.get(`${BACKEND_URL}/api/emails`, {
        params: {
          folder,
          account_id: selectedAccount.id,
          limit: 100
        },
        headers: getAuthHeader()
      });
      
      // Remove _id field if present
      const cleanedEmails = response.data.map(email => {
        const { _id, ...rest } = email;
        return rest;
      });
      
      setEmails(cleanedEmails);
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncEmails = async () => {
    if (!selectedAccount) return;
    
    try {
      setSyncing(true);
      await axios.post(
        `${BACKEND_URL}/api/emails/${selectedAccount.id}/sync`,
        {},
        { headers: getAuthHeader() }
      );
      await fetchEmails();
      showNotification('success', 'Sync Successful', 'Emails synced successfully!');
    } catch (error) {
      console.error('Error syncing emails:', error);
      showNotification('error', 'Sync Failed', error.response?.data?.detail || 'Failed to sync emails');
    } finally {
      setSyncing(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setAttachments([...attachments, ...files]);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const sendEmail = async () => {
    try {
      setSendingEmail(true);
      
      // Add any remaining input text as chips before sending
      if (toInput.trim()) {
        addEmailChip(toInput.trim(), 'to');
      }
      if (ccInput.trim()) {
        addEmailChip(ccInput.trim(), 'cc');
      }
      if (bccInput.trim()) {
        addEmailChip(bccInput.trim(), 'bcc');
      }
      
      // Get plain text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = emailBody;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('to', toChips.join(','));
      formData.append('cc', ccChips.join(',') || '');
      formData.append('bcc', bccChips.join(',') || '');
      formData.append('subject', composeData.subject);
      formData.append('body_html', emailBody);
      formData.append('body_text', textContent);
      
      // Add attachments
      attachments.forEach((file) => {
        formData.append('files', file);
      });

      await axios.post(
        `${BACKEND_URL}/api/emails/send?account_id=${selectedAccount.id}`,
        formData,
        { 
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      showNotification('success', 'Email Sent', `Email sent successfully!${attachments.length > 0 ? ` (${attachments.length} attachment${attachments.length > 1 ? 's' : ''} included)` : ''}`);
      setShowComposeModal(false);
      setComposeData({ to: '', cc: '', bcc: '', subject: '', body: '' });
      setEmailBody('');
      setAttachments([]);
      // Reset chips
      setToChips([]);
      setCcChips([]);
      setBccChips([]);
      setToInput('');
      setCcInput('');
      setBccInput('');
      
      // Refresh sent folder
      if (activeFolder === 'sent') {
        fetchEmails();
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showNotification('error', 'Send Failed', error.response?.data?.detail || error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadAttachment = async (messageId, attachmentIndex, filename) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/emails/${messageId}/attachments/${attachmentIndex}/download`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      showNotification('error', 'Download Failed', error.response?.data?.detail || error.message);
    }
  };

  // TinyMCE editor configuration
  const editorConfig = {
    height: 400,
    menubar: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount',
      'emoticons', 'codesample'
    ],
    toolbar: 'undo redo | blocks fontfamily fontsize | ' +
      'bold italic underline strikethrough | forecolor backcolor | ' +
      'alignleft aligncenter alignright alignjustify | ' +
      'bullist numlist outdent indent | ' +
      'table link image media emoticons charmap | ' +
      'removeformat code preview fullscreen',
    font_family_formats: 'Arial=arial,helvetica,sans-serif; Courier New=courier new,courier,monospace; Georgia=georgia,palatino; Tahoma=tahoma,arial,helvetica,sans-serif; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva',
    font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
    content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; }',
    branding: false,
    promotion: false,
    license_key: 'gpl'
  };

  const openAccountForm = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setAccountForm({
        email_address: account.email_address,
        password: '',
        display_name: account.username || '',
        smtp_server: account.smtp_host || 'smtp.titan.email',
        smtp_port: account.smtp_port || 465,
        smtp_use_tls: account.smtp_use_tls !== undefined ? account.smtp_use_tls : true,
        imap_server: account.imap_host || 'imap.titan.email',
        imap_port: account.imap_port || 993,
        imap_use_tls: account.imap_use_ssl !== undefined ? account.imap_use_ssl : true,
        is_default: account.is_default || false
      });
    } else {
      setEditingAccount(null);
      setAccountForm({
        email_address: '',
        password: '',
        display_name: '',
        smtp_server: 'smtp.titan.email',
        smtp_port: 465,
        smtp_use_tls: true,
        imap_server: 'imap.titan.email',
        imap_port: 993,
        imap_use_tls: true,
        is_default: emailAccounts.length === 0
      });
    }
    setShowAccountForm(true);
  };

  const closeAccountForm = () => {
    setShowAccountForm(false);
    setEditingAccount(null);
  };

  const saveEmailAccount = async () => {
    try {
      if (!accountForm.email_address || !accountForm.password) {
        showNotification('error', 'Missing Fields', 'Email address and password are required');
        return;
      }

      setSavingAccount(true);

      if (editingAccount) {
        const updatePayload = {
          username: accountForm.display_name || accountForm.email_address,
          is_default: accountForm.is_default
        };
        
        if (accountForm.password) {
          updatePayload.smtp_password = accountForm.password;
          updatePayload.imap_password = accountForm.password;
        }
        
        await axios.put(
          `${BACKEND_URL}/api/email-accounts/${editingAccount.id}`,
          updatePayload,
          { headers: getAuthHeader() }
        );
        showNotification('success', 'Account Updated', 'Email account updated successfully!');
      } else {
        const createPayload = {
          username: accountForm.display_name || accountForm.email_address,
          email_address: accountForm.email_address,
          provider: 'titan',
          smtp_host: accountForm.smtp_server,
          smtp_port: accountForm.smtp_port,
          smtp_use_tls: accountForm.smtp_use_tls,
          smtp_username: accountForm.email_address,
          smtp_password: accountForm.password,
          imap_host: accountForm.imap_server,
          imap_port: accountForm.imap_port,
          imap_use_ssl: accountForm.imap_use_tls,
          imap_username: accountForm.email_address,
          imap_password: accountForm.password,
          sync_enabled: true,
          sync_interval_minutes: 5,
          is_default: accountForm.is_default
        };
        
        await axios.post(
          `${BACKEND_URL}/api/email-accounts`,
          createPayload,
          { headers: getAuthHeader() }
        );
        showNotification('success', 'Account Added', 'Email account added successfully!');
      }

      await fetchEmailAccounts();
      closeAccountForm();
    } catch (error) {
      console.error('Error saving email account:', error);
      showNotification('error', 'Save Failed', error.response?.data?.detail || error.message);
    } finally {
      setSavingAccount(false);
    }
  };

  const deleteEmailAccount = async (accountId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this email account?');
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/email-accounts/${accountId}`,
        { headers: getAuthHeader() }
      );
      showNotification('success', 'Account Deleted', 'Email account deleted successfully!');
      await fetchEmailAccounts();
      if (selectedAccount?.id === accountId) {
        setSelectedAccount(null);
      }
    } catch (error) {
      console.error('Error deleting email account:', error);
      showNotification('error', 'Delete Failed', error.response?.data?.detail || 'Failed to delete email account');
    }
  };

  const testConnection = async (accountId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/email-accounts/${accountId}/test-connection`,
        {},
        { headers: getAuthHeader() }
      );
      showNotification('success', 'Connection Test Successful', 'SMTP and IMAP connections are working correctly. Your email account is configured properly.');
    } catch (error) {
      console.error('Error testing connection:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      showNotification('error', 'Connection Test Failed', errorMsg + '. Please check your credentials and try again.');
    }
  };

  const openFooterSettings = async (account) => {
    try {
      // Fetch the latest account data to ensure we have all fields
      const response = await axios.get(
        `${BACKEND_URL}/api/email-accounts/${account.id}`,
        { headers: getAuthHeader() }
      );
      const latestAccount = response.data;
      
      setEditingAccount(latestAccount);
      setFooterForm({
        footer_enabled: latestAccount.footer_enabled || false,
        footer_html: latestAccount.footer_html || getDefaultFooterTemplate(latestAccount),
        footer_logo_url: latestAccount.footer_logo_url || ''
      });
      setShowFooterSettings(true);
      setShowSettings(false);
    } catch (error) {
      console.error('Error loading footer settings:', error);
      showNotification('error', 'Load Failed', error.response?.data?.detail || error.message);
    }
  };

  const getDefaultFooterTemplate = (account) => {
    return `
      <div style="font-family: Arial, sans-serif; color: #333; padding: 20px 0; border-top: 2px solid #A08EA8;">
        <p style="margin: 10px 0;"><strong>Best Regards,</strong><br/>${account.username || 'Your Name'}</p>
        <p style="margin: 10px 0; font-size: 14px;">
          ✈️ <strong>Visaasure - Travel & Immigration Specialist</strong><br/>
          📞 Phone: +1 (XXX) XXX-XXXX<br/>
          ✉️ Email: ${account.email_address}<br/>
          🌐 Website: www.visaasure.com
        </p>
        <p style="margin: 10px 0; font-size: 12px; color: #666;">
          Follow us: 
          <a href="#" style="color: #A08EA8;">Facebook</a> | 
          <a href="#" style="color: #A08EA8;">Twitter</a> | 
          <a href="#" style="color: #A08EA8;">LinkedIn</a>
        </p>
        <hr style="border: none; border-top: 1px solid #ddd; margin: 15px 0;" />
        <p style="font-size: 11px; color: #999; margin: 5px 0;">
          This email and any attachments are confidential and intended solely for the addressee. 
          If you are not the intended recipient, please notify us immediately and delete this email.
        </p>
      </div>
    `.trim();
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification('error', 'Invalid File Type', 'Please upload an image file (PNG, JPEG, GIF)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      showNotification('error', 'File Too Large', 'Image size should be less than 2MB');
      return;
    }

    try {
      setUploadingLogo(true);
      // Convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        setFooterForm({ ...footerForm, footer_logo_url: reader.result });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
      showNotification('error', 'Upload Failed', 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const saveFooterSettings = async () => {
    try {
      setSavingAccount(true);
      const response = await axios.put(
        `${BACKEND_URL}/api/email-accounts/${editingAccount.id}`,
        footerForm,
        { 
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Verify the response
      if (response.status === 200) {
        showNotification('success', 'Footer Saved', 'Footer settings saved successfully!');
        setShowFooterSettings(false);
        setEditingAccount(null);
        // Refresh email accounts list
        await fetchEmailAccounts();
      }
    } catch (error) {
      console.error('Error saving footer settings:', error);
      showNotification('error', 'Save Failed', error.response?.data?.detail || error.message);
    } finally {
      setSavingAccount(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    const cleaned = text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return cleaned.length > maxLength ? cleaned.substring(0, maxLength) + '...' : cleaned;
  };

  const markAsRead = async (emailId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/emails/${emailId}/mark-read`,
        { is_read: true },
        { headers: getAuthHeader() }
      );
      // Update local state
      setEmails(emails.map(e => e.id === emailId ? { ...e, is_read: true } : e));
      
      // Dispatch custom event to update unread count in header
      window.dispatchEvent(new CustomEvent('emailReadStatusChanged'));
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };

  const handleReply = (email, replyAll = false) => {
    const recipients = replyAll && email.to ? email.to.map(t => t.email).join(', ') : '';
    const ccRecipients = replyAll && email.cc ? email.cc.map(c => c.email).join(', ') : '';
    
    setComposeData({
      to: email.from_email,
      cc: replyAll ? (recipients + (ccRecipients ? ', ' + ccRecipients : '')) : '',
      bcc: '',
      subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`
    });
    
    // Set email body with quoted original message
    const quotedBody = `<br><br><div style="border-left: 3px solid #ccc; padding-left: 12px; margin-left: 12px; color: #666;">
      <p><strong>On ${new Date(email.date).toLocaleString()}, ${email.from_name || email.from_email} wrote:</strong></p>
      ${email.body_html || `<p>${email.body_text}</p>`}
    </div>`;
    
    setEmailBody(quotedBody);
    setSelectedEmail(null);
    setShowComposeModal(true);
  };

  const handleForward = (email) => {
    setComposeData({
      to: '',
      cc: '',
      bcc: '',
      subject: email.subject.startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject}`
    });
    
    // Set email body with forwarded message
    const forwardedBody = `<br><br><div style="border-left: 3px solid #4a90e2; padding-left: 12px; margin-left: 12px;">
      <p><strong>---------- Forwarded message ---------</strong></p>
      <p><strong>From:</strong> ${email.from_name || email.from_email} &lt;${email.from_email}&gt;</p>
      <p><strong>Date:</strong> ${new Date(email.date).toLocaleString()}</p>
      <p><strong>Subject:</strong> ${email.subject}</p>
      <p><strong>To:</strong> ${email.to?.map(t => t.email).join(', ')}</p>
      <br>
      ${email.body_html || `<p>${email.body_text}</p>`}
    </div>`;
    
    setEmailBody(forwardedBody);
    setSelectedEmail(null);
    setShowComposeModal(true);
  };

  // Folder navigation data
  const folders = [
    { id: 'inbox', label: 'Inbox', icon: Inbox, color: '#3b82f6' },
    { id: 'sent', label: 'Sent', icon: Send, color: '#10b981' },
    { id: 'drafts', label: 'Drafts', icon: FileText, color: '#f59e0b' },
    { id: 'spam', label: 'Spam', icon: AlertCircle, color: '#ef4444' },
  ];

  // Define renderAccountFormModal before it's used
  const renderAccountFormModal = () => {
    if (!showAccountForm) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>{editingAccount ? 'Edit Email Account' : 'Add Email Account'}</h3>
            <button onClick={closeAccountForm} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={20} />
            </button>
          </div>

          <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Email Address *</label>
              <input
                type="email"
                value={accountForm.email_address}
                onChange={(e) => setAccountForm({ ...accountForm, email_address: e.target.value })}
                placeholder="you@yourdomain.com"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Password *</label>
              <input
                type="password"
                value={accountForm.password}
                onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                placeholder="Your email password"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600' }}>Display Name</label>
              <input
                type="text"
                value={accountForm.display_name}
                onChange={(e) => setAccountForm({ ...accountForm, display_name: e.target.value })}
                placeholder="Your Name"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>

            {/* SMTP Settings */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#7D6882' }}>
                SMTP Settings (Outgoing Mail)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6b7280' }}>SMTP Server</label>
                  <input
                    type="text"
                    value={accountForm.smtp_server}
                    onChange={(e) => setAccountForm({ ...accountForm, smtp_server: e.target.value })}
                    placeholder="smtp.yourdomain.com"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6b7280' }}>Port</label>
                  <input
                    type="number"
                    value={accountForm.smtp_port}
                    onChange={(e) => setAccountForm({ ...accountForm, smtp_port: parseInt(e.target.value) })}
                    placeholder="465"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                Common ports: 465 (SSL), 587 (TLS), 25 (Unencrypted)
              </div>
            </div>

            {/* IMAP Settings */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#7D6882' }}>
                IMAP Settings (Incoming Mail)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6b7280' }}>IMAP Server</label>
                  <input
                    type="text"
                    value={accountForm.imap_server}
                    onChange={(e) => setAccountForm({ ...accountForm, imap_server: e.target.value })}
                    placeholder="imap.yourdomain.com"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6b7280' }}>Port</label>
                  <input
                    type="number"
                    value={accountForm.imap_port}
                    onChange={(e) => setAccountForm({ ...accountForm, imap_port: parseInt(e.target.value) })}
                    placeholder="993"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                Common ports: 993 (SSL), 143 (TLS)
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={accountForm.is_default}
                  onChange={(e) => setAccountForm({ ...accountForm, is_default: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px' }}>Set as default account</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={saveEmailAccount}
                disabled={savingAccount}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: savingAccount ? '#d1d5db' : 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: savingAccount ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {savingAccount ? 'Saving...' : (editingAccount ? 'Update' : 'Add Account')}
              </button>
              
              <button
                onClick={closeAccountForm}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (emailAccounts.length === 0 && !loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
        <Mail size={64} style={{ margin: '0 auto 24px', color: '#A08EA8' }} />
        <h2 style={{ marginBottom: '16px' }}>No Email Accounts Configured</h2>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Add your Titan Email account to start managing your emails from the CRM.
        </p>
        <button
          onClick={() => openAccountForm()}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          <Plus size={16} style={{ marginRight: '8px', display: 'inline', verticalAlign: 'middle' }} />
          Add Email Account
        </button>
        
        {/* Account form modal */}
        {showAccountForm && renderAccountFormModal()}
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 100px)', display: 'flex', background: '#f9fafb' }}>
      {/* Left Sidebar - Folders */}
      <div style={{
        width: '240px',
        background: 'white',
        borderRight: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Compose Button */}
        <div style={{ padding: '16px' }}>
          <button
            onClick={() => setShowComposeModal(true)}
            style={{
              width: '100%',
              padding: '10px',
              background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Compose
          </button>
        </div>

        {/* Account Selector */}
        {emailAccounts.length > 1 && (
          <div style={{ padding: '0 16px 12px' }}>
            <select
              value={selectedAccount?.id || ''}
              onChange={(e) => {
                const account = emailAccounts.find(a => a.id === e.target.value);
                setSelectedAccount(account);
              }}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            >
              {emailAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.email_address}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Folders List */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {folders.map(folder => {
            const folderEmails = emails.filter(e => {
              if (folder.id === 'inbox') return e.folder === 'inbox' || !e.folder;
              return e.folder === folder.id;
            });
            const unreadCount = folderEmails.filter(e => !e.is_read).length;
            
            return (
              <button
                key={folder.id}
                onClick={() => setActiveFolder(folder.id)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: activeFolder === folder.id ? '#f3f4f6' : 'transparent',
                  border: 'none',
                  borderLeft: activeFolder === folder.id ? '3px solid #A08EA8' : '3px solid transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '14px',
                  color: '#374151'
                }}
              >
                <folder.icon size={18} style={{ color: folder.color }} />
                <span style={{ flex: 1 }}>{folder.label}</span>
                {unreadCount > 0 && (
                  <span style={{
                    padding: '2px 6px',
                    background: folder.color,
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Settings Button */}
        <div style={{ borderTop: '1px solid #e5e7eb', padding: '12px 16px' }}>
          <button
            onClick={() => setShowSettings(true)}
            style={{
              width: '100%',
              padding: '8px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              color: '#6b7280'
            }}
          >
            <Settings size={18} />
            Settings
          </button>
        </div>
      </div>

      {/* Main Content - Email List */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white' }}>
        {/* Toolbar */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <button
            onClick={syncEmails}
            disabled={syncing}
            style={{
              padding: '6px 12px',
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RefreshCw size={14} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
            {syncing ? 'Syncing...' : 'Sync'}
          </button>
          
          <div style={{ flex: 1, fontSize: '13px', color: '#6b7280' }}>
            {emails.length} emails
          </div>
        </div>

        {/* Email List - Very Compact */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
          ) : emails.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              No emails found. Click Sync to fetch emails.
            </div>
          ) : (
            emails.map(email => (
              <div
                key={email.id}
                onClick={() => {
                  setSelectedEmail(email);
                  if (!email.is_read) {
                    markAsRead(email.id);
                  }
                }}
                style={{
                  padding: '8px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                  background: email.is_read ? 'white' : '#f0f9ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = email.is_read ? 'white' : '#f0f9ff'}
              >
                {/* Sender/Subject Column */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: email.is_read ? '400' : '600',
                    color: '#111827',
                    marginBottom: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {activeFolder === 'sent' ? (
                      // For sent emails, show recipients (To, CC, BCC)
                      (() => {
                        const toList = email.to?.map(t => t.email) || [];
                        const ccList = email.cc?.map(c => c.email) || [];
                        const bccList = email.bcc?.map(b => b.email) || [];
                        const allRecipients = [...toList, ...ccList, ...bccList];
                        return allRecipients.length > 0 ? `To: ${allRecipients.join(', ')}` : 'No recipients';
                      })()
                    ) : (
                      // For received emails, show sender
                      email.from_name || email.from_email
                    )}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#374151',
                    fontWeight: email.is_read ? '400' : '600',
                    marginBottom: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {email.subject || '(No Subject)'}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {truncateText(email.body_text || email.body_html, 60)}
                  </div>
                </div>

                {/* Date Column */}
                <div style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  flexShrink: 0,
                  width: '60px',
                  textAlign: 'right'
                }}>
                  {formatDate(email.date)}
                </div>

                {/* Attachment Icon */}
                {email.has_attachments && (
                  <Paperclip size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '1000px',
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '600' }}>New Message</h3>
              <button
                onClick={() => setShowComposeModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Email Fields */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              {/* To Field with Chips */}
              <div style={{ 
                marginBottom: '6px', 
                borderBottom: '1px solid #f3f4f6', 
                paddingBottom: '6px',
                position: 'relative'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px', 
                  alignItems: 'center' 
                }}>
                  <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '30px' }}>To:</span>
                  {toChips.map((email, index) => (
                    <div key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, #A08EA8 0%, #8E7A96 100%)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      gap: '4px'
                    }}>
                      <span>{email}</span>
                      <button
                        onClick={() => removeEmailChip(email, 'to')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    placeholder={toChips.length === 0 ? "Add recipients" : ""}
                    value={toInput}
                    onChange={(e) => handleEmailInputChange(e.target.value, 'to')}
                    onKeyDown={(e) => handleEmailInputKeyDown(e, 'to')}
                    onFocus={() => toInput && setShowToSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowToSuggestions(false), 200)}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  />
                </div>
                {/* Autocomplete suggestions for To */}
                {showToSuggestions && addressBook.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {addressBook.filter(addr => 
                      addr.email.toLowerCase().includes(toInput.toLowerCase()) &&
                      !toChips.includes(addr.email)
                    ).slice(0, 5).map((addr, index) => (
                      <div
                        key={index}
                        onClick={() => addEmailChip(addr.email, 'to')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        {addr.email}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cc Field with Chips */}
              <div style={{ 
                marginBottom: '6px', 
                borderBottom: '1px solid #f3f4f6', 
                paddingBottom: '6px',
                position: 'relative'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px', 
                  alignItems: 'center' 
                }}>
                  <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '30px' }}>Cc:</span>
                  {ccChips.map((email, index) => (
                    <div key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, #A08EA8 0%, #8E7A96 100%)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      gap: '4px'
                    }}>
                      <span>{email}</span>
                      <button
                        onClick={() => removeEmailChip(email, 'cc')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    placeholder={ccChips.length === 0 ? "Add Cc recipients" : ""}
                    value={ccInput}
                    onChange={(e) => handleEmailInputChange(e.target.value, 'cc')}
                    onKeyDown={(e) => handleEmailInputKeyDown(e, 'cc')}
                    onFocus={() => ccInput && setShowCcSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowCcSuggestions(false), 200)}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  />
                </div>
                {/* Autocomplete suggestions for Cc */}
                {showCcSuggestions && addressBook.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {addressBook.filter(addr => 
                      addr.email.toLowerCase().includes(ccInput.toLowerCase()) &&
                      !ccChips.includes(addr.email)
                    ).slice(0, 5).map((addr, index) => (
                      <div
                        key={index}
                        onClick={() => addEmailChip(addr.email, 'cc')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        {addr.email}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bcc Field with Chips */}
              <div style={{ 
                marginBottom: '6px', 
                borderBottom: '1px solid #f3f4f6', 
                paddingBottom: '6px',
                position: 'relative'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px', 
                  alignItems: 'center' 
                }}>
                  <span style={{ fontSize: '14px', color: '#6b7280', minWidth: '30px' }}>Bcc:</span>
                  {bccChips.map((email, index) => (
                    <div key={index} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, #A08EA8 0%, #8E7A96 100%)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '16px',
                      fontSize: '13px',
                      gap: '4px'
                    }}>
                      <span>{email}</span>
                      <button
                        onClick={() => removeEmailChip(email, 'bcc')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          padding: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    placeholder={bccChips.length === 0 ? "Add Bcc recipients" : ""}
                    value={bccInput}
                    onChange={(e) => handleEmailInputChange(e.target.value, 'bcc')}
                    onKeyDown={(e) => handleEmailInputKeyDown(e, 'bcc')}
                    onFocus={() => bccInput && setShowBccSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowBccSuggestions(false), 200)}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      fontSize: '14px',
                      minWidth: '150px'
                    }}
                  />
                </div>
                {/* Autocomplete suggestions for Bcc */}
                {showBccSuggestions && addressBook.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {addressBook.filter(addr => 
                      addr.email.toLowerCase().includes(bccInput.toLowerCase()) &&
                      !bccChips.includes(addr.email)
                    ).slice(0, 5).map((addr, index) => (
                      <div
                        key={index}
                        onClick={() => addEmailChip(addr.email, 'bcc')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: index < 4 ? '1px solid #f3f4f6' : 'none',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                      >
                        {addr.email}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <input
                type="text"
                placeholder="Subject"
                value={composeData.subject}
                onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                style={{
                  width: '100%',
                  padding: '6px 0',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  outline: 'none'
                }}
              />
            </div>

            {/* Rich Text Editor - TinyMCE */}
            <div style={{ 
              flex: 1,
              overflow: 'hidden',
              padding: '12px 20px',
              minHeight: 0
            }}>
              <Editor
                tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                value={emailBody}
                onEditorChange={(content) => setEmailBody(content)}
                init={editorConfig}
              />
            </div>

            {/* Attachments Area */}
            <div
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb',
                flexShrink: 0,
                maxHeight: '120px',
                overflowY: 'auto'
              }}
            >
              {attachments.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        marginRight: '6px',
                        marginBottom: '6px',
                        fontSize: '12px'
                      }}
                    >
                      <Paperclip size={12} />
                      <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </span>
                      <button
                        onClick={() => removeAttachment(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '5px 10px',
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Paperclip size={13} />
                Attach Files
              </button>
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '14px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowComposeModal(false)}
                style={{
                  padding: '9px 18px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '7px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={sendingEmail || (toChips.length === 0 && !toInput) || !composeData.subject}
                style={{
                  padding: '9px 20px',
                  background: (sendingEmail || (toChips.length === 0 && !toInput) || !composeData.subject) ? '#d1d5db' : 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '7px',
                  cursor: (sendingEmail || (toChips.length === 0 && !toInput) || !composeData.subject) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px'
                }}
              >
                <Send size={15} />
                {sendingEmail ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '20px', flex: 1, paddingRight: '20px' }}>
                  {selectedEmail.subject || '(No Subject)'}
                </h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => handleReply(selectedEmail, false)}
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title="Reply"
                  >
                    <Mail size={14} />
                    Reply
                  </button>
                  <button
                    onClick={() => handleReply(selectedEmail, true)}
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                    title="Reply All"
                  >
                    Reply All
                  </button>
                  <button
                    onClick={() => handleForward(selectedEmail)}
                    style={{
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title="Forward"
                  >
                    <Send size={14} />
                    Forward
                  </button>
                  <button
                    onClick={() => setSelectedEmail(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>From:</span> {selectedEmail.from_name || selectedEmail.from_email} &lt;{selectedEmail.from_email}&gt;
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>To:</span> {selectedEmail.to?.map(t => t.email).join(', ')}
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#374151' }}>Date:</span> {new Date(selectedEmail.date).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Body */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '24px'
            }}>
              {selectedEmail.body_html ? (
                <div dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
              ) : (
                <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
                  {selectedEmail.body_text}
                </pre>
              )}
            </div>

            {/* Attachments */}
            {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>Attachments:</div>
                {selectedEmail.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      marginRight: '8px',
                      marginBottom: '8px',
                      fontSize: '13px'
                    }}
                  >
                    <Paperclip size={14} />
                    <span style={{ marginRight: '8px' }}>{attachment.filename}</span>
                    <span style={{ color: '#6b7280', fontSize: '12px' }}>
                      ({(attachment.size / 1024).toFixed(1)} KB)
                    </span>
                    <button
                      onClick={() => downloadAttachment(selectedEmail.id, index, attachment.filename)}
                      style={{
                        marginLeft: '8px',
                        padding: '4px 8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Download size={12} />
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Email Account Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              <button
                onClick={() => { setShowSettings(false); openAccountForm(); }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Plus size={18} />
                Add Email Account
              </button>

              {emailAccounts.map(account => (
                <div
                  key={account.id}
                  style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '15px' }}>
                        {account.username || account.email_address}
                        {account.is_default && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 6px',
                            background: '#A08EA8',
                            color: 'white',
                            fontSize: '10px',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '8px' }}>
                        {account.email_address}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        SMTP: {account.smtp_host}:{account.smtp_port} | IMAP: {account.imap_host}:{account.imap_port}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => testConnection(account.id)}
                        style={{
                          padding: '6px 10px',
                          background: '#f0fdf4',
                          border: '1px solid #86efac',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: '#16a34a'
                        }}
                        title="Test Connection"
                      >
                        <Check size={14} />
                      </button>
                      
                      <button
                        onClick={() => { setShowSettings(false); openAccountForm(account); }}
                        style={{
                          padding: '6px 10px',
                          background: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Edit Account"
                      >
                        <Edit size={14} />
                      </button>
                      
                      <button
                        onClick={() => openFooterSettings(account)}
                        style={{
                          padding: '6px 10px',
                          background: '#fef3c7',
                          border: '1px solid #fbbf24',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: '#92400e'
                        }}
                        title="Configure Email Footer"
                      >
                        <FileText size={14} />
                      </button>
                      
                      <button
                        onClick={() => deleteEmailAccount(account.id)}
                        style={{
                          padding: '6px 10px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          color: '#dc2626',
                          fontSize: '12px'
                        }}
                        title="Delete Account"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Account Form Modal */}
      {renderAccountFormModal()}

      {/* Footer Settings Modal */}
      {showFooterSettings && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Email Footer / Signature Configuration</h3>
              <button
                onClick={() => setShowFooterSettings(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Enable Footer Toggle */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={footerForm.footer_enabled}
                    onChange={(e) => setFooterForm({ ...footerForm, footer_enabled: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>Enable Email Footer</span>
                </label>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', marginLeft: '28px' }}>
                  Automatically append this footer to all outgoing emails (compose, reply, forward)
                </p>
              </div>

              {/* Company Logo Upload */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                  Company Logo (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    style={{
                      padding: '8px 16px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: uploadingLogo ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Upload size={14} />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </button>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                  {footerForm.footer_logo_url && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <img
                        src={footerForm.footer_logo_url}
                        alt="Logo preview"
                        style={{ maxHeight: '50px', maxWidth: '150px', objectFit: 'contain' }}
                      />
                      <button
                        onClick={() => setFooterForm({ ...footerForm, footer_logo_url: '' })}
                        style={{
                          padding: '4px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                  Supported: PNG, JPEG, GIF (Max 2MB)
                </p>
              </div>

              {/* Footer HTML Editor */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                  Email Footer Content
                </label>
                <Editor
                  tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                  value={footerForm.footer_html}
                  onEditorChange={(content) => setFooterForm({ ...footerForm, footer_html: content })}
                  init={{
                    height: 300,
                    menubar: false,
                    plugins: [
                      'lists', 'link', 'image', 'code', 'table'
                    ],
                    toolbar: 'undo redo | formatselect | bold italic | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link image | code',
                    content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }',
                    branding: false,
                    promotion: false,
                    license_key: 'gpl'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                  Customize your email footer with company info, contact details, and social links
                </p>
              </div>

              {/* Preview */}
              {footerForm.footer_html && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                    Preview
                  </label>
                  <div
                    style={{
                      padding: '16px',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  >
                    {footerForm.footer_logo_url && (
                      <div style={{ marginBottom: '12px' }}>
                        <img
                          src={footerForm.footer_logo_url}
                          alt="Company Logo"
                          style={{ maxHeight: '60px', maxWidth: '200px' }}
                        />
                      </div>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: footerForm.footer_html }} />
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowFooterSettings(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveFooterSettings}
                  disabled={savingAccount}
                  style={{
                    padding: '10px 20px',
                    background: savingAccount ? '#d1d5db' : 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: savingAccount ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  {savingAccount ? 'Saving...' : 'Save Footer Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            transform: translateY(20px);
            opacity: 0;
          }
          to { 
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .tox-tinymce {
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
        }
        
        .tox .tox-toolbar {
          background: #f9fafb !important;
        }
        
        .tox .tox-tbtn {
          border-radius: 4px !important;
        }
        
        .tox .tox-tbtn:hover {
          background: #e5e7eb !important;
        }
        
        .tox .tox-tbtn--enabled {
          background: #A08EA8 !important;
          color: white !important;
        }
      `}</style>

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease-in'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: notification.type === 'success' 
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : notification.type === 'error'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : notification.type === 'warning'
                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'white'
            }}>
              {notification.type === 'success' ? '✓' : 
               notification.type === 'error' ? '✕' :
               notification.type === 'warning' ? '⚠' : 'ℹ'}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              textAlign: 'center'
            }}>
              {notification.title}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#6b7280',
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              {notification.message}
            </p>

            {/* Close Button */}
            <button
              onClick={closeNotification}
              style={{
                width: '100%',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
